import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";

const STYLE = {
  "not emailed": "bg-stone-100 text-stone-700", sent: "bg-sky-100 text-sky-800", reminded: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800", expired: "bg-stone-200 text-stone-600", cancelled: "bg-stone-200 text-stone-600",
};
const FILTERS = ["all", "not emailed", "sent", "reminded", "paid", "expired"];
const PAGE = 20;
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }) : "");

const Timeline = ({ x }) => (
  <div className="text-xs text-ink-muted leading-5">
    <div>Created {when(x.created_at)}{x.created_by && ` by ${x.created_by}`}</div>
    {x.emailed_at && <div>Emailed {when(x.emailed_at)}</div>}
    {x.reminded_at && <div>Reminder sent {when(x.reminded_at)}</div>}
    {x.paid_at ? <div className="text-emerald-700">Paid {when(x.paid_at)}</div> : x.state !== "cancelled" && <div>{x.state === "expired" ? "Expired" : "Expires"} {when(x.expires_at)}</div>}
  </div>
);

const Row = ({ x }) => (
  <tr className="border-t border-stone-200/70 align-top text-sm" data-testid={`link-status-row-${x.id}`}>
    <td className="py-3 pr-3"><span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STYLE[x.state]}`} data-testid={`link-status-state-${x.id}`}>{x.state}</span></td>
    <td className="py-3 pr-3"><div className="font-medium">{x.patient_name}</div><div className="text-xs text-ink-muted">{x.email || "no email"}</div></td>
    <td className="py-3 pr-3 text-xs">{x.kind}{x.appointment && <div className="text-ink-muted">for {when(x.appointment)}</div>}</td>
    <td className="py-3 pr-3 font-medium">£{(x.amount / 100).toFixed(2)}</td>
    <td className="py-3"><Timeline x={x} /></td>
  </tr>
);

export const LinkStatusBoard = ({ refreshKey }) => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const load = useCallback(() => { api.get("/admin/link-status").then(({ data }) => setRows(data.links)).catch(() => setRows([])); }, []);
  useEffect(load, [load, refreshKey]);
  const shown = useMemo(() => (rows || []).filter((x) => filter === "all" || x.state === filter || (filter === "expired" && x.state === "cancelled")), [rows, filter]);
  const pick = (f) => { setFilter(f); setLimit(PAGE); };
  return (
    <section className="bg-white border border-stone-200 rounded-md p-6 overflow-x-auto" data-testid="link-status-board">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-serif text-2xl mr-3">Payment link status</h2>
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => pick(f)} data-testid={`link-status-filter-${f.replace(" ", "-")}`}
            className={`text-xs px-3 py-1 rounded-full border capitalize transition-colors ${filter === f ? "bg-bronze-dark text-white border-bronze-dark" : "bg-white border-stone-300 hover:border-bronze-dark"}`}>
            {f} {rows && f !== "all" && `(${rows.filter((x) => x.state === f || (f === "expired" && x.state === "cancelled")).length})`}
          </button>
        ))}
        <button type="button" onClick={load} className="ml-auto text-sm underline" data-testid="link-status-refresh">Refresh</button>
      </div>
      <p className="text-xs text-ink-muted mt-1">Every test booking link and payment request, newest first. Test booking links get an automatic reminder 4 hours before they expire.</p>
      <table className="mt-3 w-full text-left">
        <thead><tr className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">
          {["Status", "Patient", "Type", "Amount", "Timeline"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>{shown.slice(0, limit).map((x) => <Row key={x.id} x={x} />)}</tbody>
      </table>
      {shown.length > limit && <button type="button" onClick={() => setLimit(limit + PAGE)} className="mt-3 text-sm underline" data-testid="link-status-more">Show more ({shown.length - limit} more)</button>}
      {rows && !shown.length && <p className="text-sm text-ink-muted mt-2" data-testid="link-status-empty">No links here.</p>}
    </section>
  );
};
